import React from 'react'
import { ActionResult, ADMIN_SECTIONS, AdminAction } from './AdminTools.actions'

import { saveAs } from 'file-saver'
import { decodeBlobErrorBody, filenameFromContentDisposition, requestOptions } from './AdminTools.download'
import { GRAY_700 } from 'constants/styling/theme'
import { AdminToolsActionCard } from './AdminToolsActionCard.component'
import { PageTransition } from 'utils/animations'
import { TokenPanel } from './TokenPanel.component'
import Typography from '@mui/material/Typography'
import styles from './AdminTools.module.sass'
import { useAPI } from 'utils/API'
import { useAuth0 } from 'utils/auth'

const AdminTools: React.FC = () => {
  const { roles } = useAuth0()
  const api = useAPI<string>()

  const run = async (
    action: AdminAction,
    values: Record<string, string>,
    files: Record<string, File>
  ): Promise<ActionResult> => {
    const request = action.buildRequest(values, files)
    const { path, data } = request
    const options = requestOptions(action, request)
    const call = () =>
      action.method === 'GET' ? api.get<unknown>(path, {}, true, options)
        : action.method === 'PUT' ? api.put<unknown>(path, {}, data, true, options)
          : api.post<unknown>(path, {}, data ?? {}, true, options)
    const response = await call().catch(async error => { throw await decodeBlobErrorBody(error) })
    if (action.download) {
      const filename = filenameFromContentDisposition(response.headers['content-disposition'], `${action.id}.csv`)
      saveAs(response.data as Blob, filename)
      return { status: response.status, data: `Downloaded ${filename}` }
    }
    return { status: response.status, data: response.data }
  }

  if (!roles.isAdmin) return (
    <PageTransition>
      <div className="page">
        <div className="page-content">
          <Typography variant="display-md">ADMIN ONLY AREA</Typography>
        </div>
      </div>
    </PageTransition>
  )

  return (
    <PageTransition>
      <div className="page">
        <div className="page-content">
          <div className={styles.container}>
            <header>
              <Typography variant="display-md">Admin tools</Typography>
              <Typography variant="text-sm" color={GRAY_700}>
                Operational actions. All calls run against the current API environment.
              </Typography>
            </header>

            <section className={styles.section}>
              <Typography variant="h3">Auth token</Typography>
              <TokenPanel />
            </section>

            {ADMIN_SECTIONS.map(section => (
              <section key={section.id} className={styles.section}>
                <Typography variant="h3">{section.title}</Typography>
                {section.description &&
                  <Typography variant="text-sm" color={GRAY_700}>{section.description}</Typography>
                }
                <div className={styles.cardGrid}>
                  {section.actions.map(action => (
                    <AdminToolsActionCard key={action.id} action={action} run={run} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </PageTransition>
  )
}

export default AdminTools
